<template>
  <b-card v-if="loading" data-e2e="profile-big-score-loading">
    <div class="animate-pulse flex items-center gap-6">
      <div class="h-16 w-24 bg-gray-700 rounded"></div>
      <div class="flex-1 space-y-2">
        <div class="h-4 bg-gray-700 rounded w-32"></div>
        <div class="h-3 bg-gray-700 rounded w-48"></div>
      </div>
    </div>
  </b-card>

  <b-card v-else-if="bigScore && bigScore.score !== null" data-e2e="profile-big-score-card">
    <!-- Header: title + expertise-score subtitle + how-it-works link + visibility toggle -->
    <div class="flex items-start justify-between mb-4">
      <div class="flex flex-col gap-1 min-w-0">
        <span class="uppercase text-gray-300 text-lg leading-none">{{ t('big_score.title') }}</span>
        <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span class="text-sm text-gray-400" data-e2e="big-score-subtitle">{{ t('big_score.subtitle') }}</span>
          <button
            @click="helpModalOpen = true"
            class="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-200 transition-colors cursor-pointer"
            data-e2e="big-score-help-button"
          >
            <Icon name="solar:info-circle-bold-duotone" size="1.125rem" />
            {{ t('big_score.how_it_works') }}
          </button>
        </div>
      </div>
      <div v-if="isOwner" class="flex items-center gap-2 shrink-0">
        <BSwitch
          v-model="scoreIsPublic"
          :label="scoreIsPublic ? t('big_score.visibility_public') : t('big_score.visibility_private')"
          size="sm"
        />
      </div>
    </div>

    <!-- Horizontal layout: score | context | CTAs -->
    <div class="flex flex-col md:flex-row items-center gap-6">
      <!-- Score -->
      <div class="flex items-baseline gap-1 shrink-0">
        <span class="text-5xl font-extrabold text-white leading-none" data-e2e="big-score-value">{{ bigScore.score }}</span>
        <span class="text-lg text-gray-400 font-medium">/100</span>
      </div>

      <!-- Separator -->
      <div class="hidden md:block w-px h-12 bg-gray-700"></div>

      <!-- Top context -->
      <div v-if="bigScore.top_contexts.length > 0" class="flex-1 min-w-0">
        <p class="text-xs text-gray-400 font-medium mb-2">{{ t('big_score.top_context') }}</p>
        <div class="flex flex-wrap gap-1.5">
          <span
            v-for="badge in topContextBadges"
            :key="badge.label"
            class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-gray-900 border border-gray-700 text-gray-200"
            data-e2e="big-score-context-badge"
          >
            <Icon :name="badge.icon" size="0.85rem" class="text-gray-400 shrink-0" />
            {{ badge.label }}
          </span>
        </div>
      </div>

      <!-- CTAs (owner only) -->
      <div v-if="isOwner" class="flex gap-2 shrink-0">
        <BButton variant="primary" light size="sm" @click="shareModalOpen = true" data-e2e="big-score-share-button">
          <Icon name="solar:share-bold-duotone" size="1rem" class="mr-2" />
          {{ t('big_score.share') }}
        </BButton>
        <BButton variant="secondary" light size="sm" @click="improveModalOpen = true" data-e2e="big-score-improve-button">
          <Icon name="solar:chart-2-bold-duotone" size="1rem" class="mr-2" />
          {{ t('big_score.improve') }}
        </BButton>
      </div>
    </div>

    <!-- Carte source hors écran : toujours montée pour l'owner, sert à la rasterisation.
         On capture le div wrapper (Element garanti), pas le composant. -->
    <div v-if="isOwner" aria-hidden="true" style="position: fixed; left: -9999px; top: 0; pointer-events: none;">
      <div ref="shareCard">
        <BBigScoreShareCard
          :score="bigScore?.score ?? 0"
          :top-skills="(bigScore?.top_skills ?? []).slice(0, 5)"
          :context="shareContext"
          :user-name="userName"
          :title="title"
          :photo-data-url="photoDataUrl"
        />
      </div>
    </div>
  </b-card>

  <!-- Empty state (owner only) -->
  <b-card v-else-if="isOwner" data-e2e="profile-big-score-empty">
    <div class="flex flex-col md:flex-row items-center gap-6">
      <div class="uppercase text-gray-300 text-lg shrink-0">{{ t('big_score.title') }}</div>
      <div class="hidden md:block w-px h-8 bg-gray-700"></div>
      <p class="text-sm text-gray-400 flex-1">{{ t('big_score.empty_description') }}</p>
      <BButton variant="primary" light size="sm" @click="openCreateExperience" data-e2e="big-score-add-experience-button">
        {{ t('big_score.empty_cta') }}
      </BButton>
    </div>
  </b-card>

  <!-- Share Modal -->
  <BModal v-model="shareModalOpen" :title="t('big_score.share_modal_title')" no-footer size="2xl">
    <div class="p-6 space-y-5">
      <!-- Aperçu : image générée, responsive (jamais coupée) -->
      <div class="w-full rounded-lg overflow-hidden bg-gray-900 flex items-center justify-center">
        <img v-if="previewSrc" :src="previewSrc" alt="" class="w-full h-auto block" />
        <div v-else-if="previewLoading" class="py-12 text-sm text-gray-400">{{ t('big_score.share_modal_generating') }}</div>
        <div v-else class="py-12 text-sm text-gray-400">{{ t('big_score.image_error') }}</div>
      </div>

      <!-- Texte d'exemple à partager sur les réseaux -->
      <div>
        <div class="flex items-center justify-between mb-2">
          <p class="text-xs text-gray-400">{{ t('big_score.share_modal_draft_label') }}</p>
          <BButton size="sm" prepend-icon="solar:copy-linear" @click="copyDraft">
            {{ draftCopied ? t('big_score.copied') : t('big_score.copy') }}
          </BButton>
        </div>
        <textarea
          readonly
          rows="6"
          :value="draftMessage"
          class="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-sm text-gray-300 resize-none focus:outline-none"
        />
      </div>

      <!-- Lien vers le profil -->
      <div>
        <p class="text-xs text-gray-400 mb-2">{{ t('big_score.share_modal_url_label') }}</p>
        <div class="flex items-center justify-between gap-2 bg-gray-900 border border-gray-700 rounded-lg pl-4 pr-2 py-2">
          <span class="min-w-0 truncate text-sm text-gray-300">{{ profileUrl }}</span>
          <BButton size="sm" prepend-icon="solar:copy-linear" @click="copyProfileUrl">
            {{ urlCopied ? t('copy_link.copied') : t('copy_link.copy') }}
          </BButton>
        </div>
      </div>

      <!-- Téléchargement de l'image -->
      <div class="flex gap-2">
        <BButton variant="primary" light size="sm" class="flex-1" @click="handleDownload">
          <Icon name="solar:download-bold-duotone" size="1rem" class="mr-2" />
          {{ t('big_score.share_modal_download') }}
        </BButton>
      </div>

      <p class="text-xs text-gray-400 text-center">{{ t('big_score.share_modal_hint') }}</p>
    </div>
  </BModal>

  <!-- Improve Modal -->
  <BModal v-model="improveModalOpen" :title="t('big_score.improve_modal_title')" no-footer size="md">
    <div class="p-6">
      <div v-if="bigScore?.suggestions && bigScore.suggestions.length > 0" class="space-y-4">
        <div
          v-for="(suggestion, i) in bigScore.suggestions"
          :key="i"
          class="flex items-start gap-3 p-3 bg-gray-700/50 rounded-lg"
        >
          <Icon name="solar:lightbulb-bolt-bold-duotone" size="1.25rem" class="text-yellow-400 mt-0.5 shrink-0" />
          <p class="text-sm text-gray-200">{{ suggestion }}</p>
        </div>
      </div>
      <div v-else class="text-center py-4">
        <Icon name="solar:check-circle-bold-duotone" size="2rem" class="text-green-400 mb-2" />
        <p class="text-sm text-gray-300">{{ t('big_score.improve_modal_complete') }}</p>
      </div>
    </div>
  </BModal>

  <!-- Help Modal -->
  <BModal v-model="helpModalOpen" :title="t('big_score.help_modal_title')" no-footer size="md">
    <div class="p-6 space-y-4">
      <p class="text-sm text-gray-200">{{ t('big_score.help_modal_intro') }}</p>
      <div class="space-y-3">
        <div class="flex items-start gap-3">
          <Icon name="solar:star-bold-duotone" size="1.25rem" class="text-purple-400 mt-0.5 shrink-0" />
          <div>
            <p class="text-sm font-medium text-white">{{ t('big_score.help_modal_what_title') }}</p>
            <p class="text-xs text-gray-400 mt-1">{{ t('big_score.help_modal_what_desc') }}</p>
          </div>
        </div>
        <div class="flex items-start gap-3">
          <Icon name="solar:chart-2-bold-duotone" size="1.25rem" class="text-purple-400 mt-0.5 shrink-0" />
          <div>
            <p class="text-sm font-medium text-white">{{ t('big_score.help_modal_how_title') }}</p>
            <p class="text-xs text-gray-400 mt-1">{{ t('big_score.help_modal_how_desc') }}</p>
          </div>
        </div>
        <div class="flex items-start gap-3">
          <Icon name="solar:shield-check-bold-duotone" size="1.25rem" class="text-purple-400 mt-0.5 shrink-0" />
          <div>
            <p class="text-sm font-medium text-white">{{ t('big_score.help_modal_trust_title') }}</p>
            <p class="text-xs text-gray-400 mt-1">{{ t('big_score.help_modal_trust_desc') }}</p>
          </div>
        </div>
      </div>
    </div>
  </BModal>
</template>

<script setup lang="ts">
import BCard from "~/components/card/BCard.vue";
import BButton from "~/components/forms/BButton.vue";
import BModal from "~/components/modal/BModal.vue";
import BSwitch from "~/components/forms/BSwitch.vue";
import BBigScoreShareCard from "~/components/card/BBigScoreShareCard.vue";
import type { BigScoreResponse } from "~/api/v2/big-score/BigScoreRepository";
import { useBigScoreImage } from "~/composables/useBigScoreImage";

const props = defineProps<{
  slug: string;
  isOwner: boolean;
  userName: string;
  title?: string | null;
  photoUrl?: string | null;
}>();

const { t } = useI18n();
const { $api } = useNuxtApp();
const config = useRuntimeConfig();

const bigScore = ref<BigScoreResponse | null>(null);
const loading = ref(true);
const shareModalOpen = ref(false);
const improveModalOpen = ref(false);
const helpModalOpen = ref(false);
const scoreIsPublic = ref(false);

const { generateBlob, downloadImage, fetchImageAsDataUrl } = useBigScoreImage();
const shareCard = ref<HTMLElement | null>(null);
const photoDataUrl = ref<string | null>(null);

// Aperçu : on affiche le PNG généré en <img> responsive (jamais coupé).
const previewSrc = ref<string | null>(null);
const previewLoading = ref(false);
const draftCopied = ref(false);
const urlCopied = ref(false);
let draftCopyTimeout: ReturnType<typeof setTimeout> | null = null;
let urlCopyTimeout: ReturnType<typeof setTimeout> | null = null;

const revokePreview = () => {
    if (previewSrc.value) {
        URL.revokeObjectURL(previewSrc.value);
        previewSrc.value = null;
    }
};
const experienceCreationIsOpen = inject<Ref<boolean>>('experienceCreationIsOpen', ref(false));

const openCreateExperience = () => {
    experienceCreationIsOpen.value = true;
};

const profileUrl = computed(() => `${config.public.NUXT_APP_URL}account/${props.slug}`);

// Texte d'exemple (template) que le freelance peut copier-coller sur ses réseaux.
const draftMessage = computed(() => {
    const skills = (bigScore.value?.top_skills ?? []).join(' · ');
    return t('big_score.share_modal_draft', {
        score: bigScore.value?.score ?? 0,
        skills: skills || t('big_score.share_modal_draft_skills_fallback'),
        url: profileUrl.value,
    });
});

const topContextBadges = computed(() => {
    if (!bigScore.value || bigScore.value.top_contexts.length === 0) return [];
    const ctx = bigScore.value.top_contexts[0];
    const badges: { icon: string; label: string }[] = [
        { icon: "solar:star-linear", label: ctx.skill.name },
    ];
    if (ctx.industry) badges.push({ icon: "solar:buildings-3-linear", label: ctx.industry.name });
    if (ctx.persona) badges.push({ icon: "solar:user-rounded-linear", label: ctx.persona.name });
    if (ctx.market_segment) badges.push({ icon: "solar:buildings-2-linear", label: ctx.market_segment.name });
    if (ctx.deal_size) badges.push({ icon: "solar:dollar-linear", label: ctx.deal_size.name });
    return badges;
});

// Share card uses logical icon keys (mapped to inline SVG inside the card,
// because the image is rasterized). Skill stays on its own "Top compétences"
// row, so we only pass industry/persona/segment/deal here.
const shareContext = computed(() => {
    if (!bigScore.value || bigScore.value.top_contexts.length === 0) return [];
    const ctx = bigScore.value.top_contexts[0];
    const items: { icon: string; label: string }[] = [];
    if (ctx.industry) items.push({ icon: "industry", label: ctx.industry.name });
    if (ctx.persona) items.push({ icon: "persona", label: ctx.persona.name });
    if (ctx.market_segment) items.push({ icon: "market_segment", label: ctx.market_segment.name });
    if (ctx.deal_size) items.push({ icon: "deal_size", label: ctx.deal_size.name });
    return items;
});

const fetchBigScore = async () => {
    loading.value = true;
    try {
        bigScore.value = await $api().v2.bigScore.get(props.slug);
        if (bigScore.value) {
            scoreIsPublic.value = bigScore.value.is_visible;
        }
    } catch {
        bigScore.value = null;
    } finally {
        loading.value = false;
    }
};

watch(shareModalOpen, async (open) => {
    if (!open) {
        revokePreview();
        return;
    }
    previewLoading.value = true;
    if (props.photoUrl && !photoDataUrl.value) {
        photoDataUrl.value = await fetchImageAsDataUrl(props.photoUrl);
    }
    await nextTick();
    const blob = await generateBlob(shareCard.value);
    revokePreview();
    if (blob) previewSrc.value = URL.createObjectURL(blob);
    previewLoading.value = false;
});

onUnmounted(() => {
    revokePreview();
    if (draftCopyTimeout) clearTimeout(draftCopyTimeout);
    if (urlCopyTimeout) clearTimeout(urlCopyTimeout);
});

const handleDownload = async () => {
    const el = shareCard.value;
    const alerts = useAlerts();
    const safeName = props.userName.trim().toLowerCase().replace(/\s+/g, "-") || "profil";
    const success = await downloadImage(el, `big-score-${safeName}.png`);
    alerts.add(
        success
            ? { type: 'success', title: t('big_score.image_downloaded') }
            : { type: 'danger', title: t('big_score.image_error') }
    );
};

const copyDraft = async () => {
    try {
        await navigator.clipboard.writeText(draftMessage.value);
        draftCopied.value = true;
        if (draftCopyTimeout) clearTimeout(draftCopyTimeout);
        draftCopyTimeout = setTimeout(() => { draftCopied.value = false; }, 2000);
    } catch {
        const alerts = useAlerts();
        alerts.add({ type: 'danger', title: t('big_score.url_copy_error') });
    }
};

const copyProfileUrl = async () => {
    try {
        await navigator.clipboard.writeText(profileUrl.value);
        urlCopied.value = true;
        if (urlCopyTimeout) clearTimeout(urlCopyTimeout);
        urlCopyTimeout = setTimeout(() => { urlCopied.value = false; }, 2000);
    } catch {
        const alerts = useAlerts();
        alerts.add({ type: 'danger', title: t('big_score.url_copy_error') });
    }
};

let skipVisibilityWatch = true;
watch(scoreIsPublic, async (newVal) => {
    if (skipVisibilityWatch) return;
    try {
        await $api().v2.bigScore.updateVisibility(props.slug, newVal);
    } catch {
        scoreIsPublic.value = !newVal;
    }
});

onMounted(() => {
    fetchBigScore().then(() => { skipVisibilityWatch = false; });
});

watch(() => props.slug, () => {
    skipVisibilityWatch = true;
    fetchBigScore().then(() => { skipVisibilityWatch = false; });
});
</script>
